// ✅ src/components/UploadDocumentos.jsx
// PROD SAFE + SCAN AGENT INTEGRADO + PAGE SIZE (Carta/OficioMX/Legal/A4/Auto/Custom)
// - DEFAULT EXPORT ✅
// - Todo dentro del archivo ✅
// - UX/UI PRO: simple, jerarquía clara, acción primaria sticky ✅

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { getUploadUrlDocs, confirmUploadDocs } from "../services/api";

// =========================
// UI TOKENS (Premium)
// =========================
const UI = {
  page: "bg-slate-50 text-slate-900",
  card: "bg-white ring-1 ring-slate-200 shadow-sm rounded-2xl",
  border: "border border-slate-200",
  input:
    "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-700 focus:ring-4 focus:ring-blue-100",
  select:
    "w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-700 focus:ring-4 focus:ring-blue-100",
  label: "text-xs font-medium text-slate-600",
  hint: "text-xs text-slate-500",

  btnPrimary:
    "bg-blue-900 text-white hover:bg-blue-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed",
  btnSecondary:
    "bg-white text-slate-900 ring-1 ring-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed",

  chipNeutral: "bg-slate-100 text-slate-700",
  chipInfo: "bg-blue-50 text-blue-800 ring-1 ring-blue-200",
  chipOk: "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200",
  chipWarn: "bg-amber-50 text-amber-900 ring-1 ring-amber-200",
  chipErr: "bg-rose-50 text-rose-800 ring-1 ring-rose-200",

  calloutWarn: "bg-amber-50 text-amber-900 ring-1 ring-amber-200",
  calloutInfo: "bg-blue-50 text-blue-900 ring-1 ring-blue-200",
  calloutErr: "bg-rose-50 text-rose-900 ring-1 ring-rose-200",

  progressTrack: "bg-slate-100 ring-1 ring-slate-200",
  progressBar: "bg-blue-900", // 👈 único acento
};

/* =========================
   ScanAgent config (LOCAL)
========================= */
const SCAN_AGENT_BASE =
  process.env.REACT_APP_SCAN_AGENT_BASE || "http://127.0.0.1:17777";
const SCAN_AGENT_TOKEN = process.env.REACT_APP_SCAN_AGENT_TOKEN || "111111";
const scanHeaders = () => ({ "X-ScanAgent-Token": SCAN_AGENT_TOKEN });

async function pollScanStatusCompat(
  startPayload,
  {
    timeoutMs = 12 * 60 * 1000,     // 12 min para 20 hojas (ajusta)
    detectEndpointMs = 8000,
    minLoopDelayMs = 350,

    // polling base
    fastIntervalMs = 1200,          // al inicio
    normalIntervalMs = 2500,        // estable
    slowIntervalMs = 5000,          // si no hay cambios
    maxIntervalMs = 8000,           // techo

    // /wait
    longPollTimeoutMs = 25000,
    waitMustTakeAtLeastMs = 2000,   // si /wait responde <2s -> NO es long-poll real

    // ramp windows
    fastWindowMs = 30 * 1000,       // primeros 30s rápido
    noChangeToSlowMs = 20 * 1000,   // si 20s sin cambios -> lento
    noChangeToMaxMs = 45 * 1000,    // si 45s sin cambios -> más lento
  } = {}
) {
  const started = Date.now();
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const candidates = [];

  // API vieja
  if (startPayload?.statusUrl) {
    const u = String(startPayload.statusUrl);
    candidates.push({
      label: "statusUrl",
      url: u.startsWith("http") ? u : `${SCAN_AGENT_BASE}${u}`,
      mode: "GET",
      kind: "poll",
    });
  }

  const jobId = startPayload?.jobId ? String(startPayload.jobId) : "";
  if (jobId) {
    const id = encodeURIComponent(jobId);

    // ✅ primero status "normal"
    candidates.push(
      { label: "GET /scan/status/:id", url: `${SCAN_AGENT_BASE}/scan/status/${id}`, mode: "GET", kind: "poll" },
      { label: "GET /scan/:id",        url: `${SCAN_AGENT_BASE}/scan/${id}`,        mode: "GET", kind: "poll" },
      { label: "GET /jobs/:id",        url: `${SCAN_AGENT_BASE}/jobs/${id}`,        mode: "GET", kind: "poll" },
      { label: "GET /status/:id",      url: `${SCAN_AGENT_BASE}/status/${id}`,      mode: "GET", kind: "poll" },

      // /wait al final (solo si de verdad espera)
      { label: "GET /scan/wait/:id",   url: `${SCAN_AGENT_BASE}/scan/wait/${id}`,   mode: "GET", kind: "wait" },

      // POST fallbacks
      { label: "POST /scan/status {jobId}", url: `${SCAN_AGENT_BASE}/scan/status`, mode: "POST", kind: "poll" },
      { label: "POST /jobs/status {jobId}", url: `${SCAN_AGENT_BASE}/jobs/status`, mode: "POST", kind: "poll" },
      { label: "POST /status {jobId}",      url: `${SCAN_AGENT_BASE}/status`,      mode: "POST", kind: "poll" }
    );
  }

  if (candidates.length === 0) throw new Error("ScanAgent no devolvió statusUrl ni jobId.");

  const requestStatus = async (cand) => {
    const t0 = Date.now();
    let res;

    if (cand.kind === "wait") {
      res = await axios.get(cand.url, {
        headers: scanHeaders(),
        timeout: longPollTimeoutMs + 5000,
        params: { timeoutMs: longPollTimeoutMs },
      });
    } else if (cand.mode === "POST") {
      res = await axios.post(
        cand.url,
        { jobId },
        { headers: { ...scanHeaders(), "Content-Type": "application/json" }, timeout: 30000 }
      );
    } else {
      res = await axios.get(cand.url, { headers: scanHeaders(), timeout: 30000 });
    }

    return { res, elapsedMs: Date.now() - t0 };
  };

  const toStatus = (payload) => String(payload?.status || "").trim().toLowerCase();

  const fingerprint = (payload) => {
    const st = toStatus(payload);

    const prog =
      payload?.progress ??
      payload?.pct ??
      payload?.percent ??
      payload?.page ??
      payload?.pageIndex ??
      payload?.currentPage ??
      payload?.pagesScanned ??
      payload?.scannedPages ??
      payload?.pages ??
      payload?.stage ??
      payload?.step ??
      "";

    const hasResult =
      !!payload?.result ||
      !!payload?.result?.url ||
      !!payload?.url ||
      !!payload?.downloadUrl;

    return `${st}|${hasResult ? "r1" : "r0"}|${prog}`;
  };

  // 1) Detect endpoint
  let working = null;
  let firstPayload = null;

  const detectStart = Date.now();
  while (!working) {
    if (Date.now() - detectStart > detectEndpointMs) {
      const list = candidates.map((c) => `- ${c.mode} ${c.url} (${c.label})`).join("\n");
      throw new Error("No encontré endpoint de STATUS para el jobId.\nIntenté:\n" + list);
    }

    for (const cand of candidates) {
      try {
        const { res, elapsedMs } = await requestStatus(cand);

        // /wait rápido => no es long-poll real, degradar
        const kindEffective =
          cand.kind === "wait" && elapsedMs < waitMustTakeAtLeastMs ? "poll" : cand.kind;

        working = { ...cand, kind: kindEffective };
        firstPayload = normalizeStatusPayload(res.data, working);
        break;
      } catch (e) {
        const code = e?.response?.status;
        if (code === 404 || code === 405) continue;
        if (code === 401 || code === 403) throw new Error(`ScanAgent auth falló (HTTP ${code}).`);
        if (code) throw new Error(normalizeError(e));
      }
    }

    if (!working) await sleep(250);
  }

  // 2) Loop principal: adaptive polling
  let lastFp = "";
  let lastChangeAt = Date.now();

  while (true) {
    if (Date.now() - started > timeoutMs) throw new Error("Timeout esperando escaneo.");

    const t0 = Date.now();

    const payload = firstPayload
      ? firstPayload
      : normalizeStatusPayload((await requestStatus(working)).res.data, working);

    firstPayload = null;

    const st = toStatus(payload);

    if (["done", "completed", "success", "ok", "finished"].includes(st)) return payload;
    if (["error", "failed", "fail"].includes(st)) throw new Error(payload?.error || "Scan falló.");

    const fp = fingerprint(payload);
    if (fp !== lastFp) {
      lastFp = fp;
      lastChangeAt = Date.now();
    }

    const elapsedReq = Date.now() - t0;
    const sinceStart = Date.now() - started;
    const sinceChange = Date.now() - lastChangeAt;

    // ✅ Decide intervalo
    let interval;

    if (working.kind === "wait") {
      // si es long-poll real, ya espera en server
      interval = longPollTimeoutMs;
    } else {
      // polling adaptativo
      if (sinceStart < fastWindowMs) interval = fastIntervalMs;
      else interval = normalIntervalMs;

      if (sinceChange > noChangeToSlowMs) interval = slowIntervalMs;
      if (sinceChange > noChangeToMaxMs) interval = maxIntervalMs;
    }

    const sleepMs = Math.max(minLoopDelayMs, interval - elapsedReq);
    await sleep(sleepMs);
  }
}

function normalizeStatusPayload(data, working) {
  const status = String(data?.status ?? data?.state ?? data?.jobStatus ?? "").trim();
  return { ...data, status: status || data?.status || "Unknown", _working: working };
}

/* =========================
   Build PDF File from status
   - url directa
   - base64 embebido
   - fallback: descargar por jobId (endpoints comunes)
========================= */
async function buildFileFromStatus(status, startPayload) {
  const jobId = startPayload?.jobId ? String(startPayload.jobId) : "";

  // 1) URL directa
  const url =
    status?.result?.url ||
    status?.url ||
    status?.downloadUrl ||
    status?.result?.downloadUrl ||
    status?.result?.fileUrl;

  const fileName =
    status?.result?.fileName ||
    status?.fileName ||
    status?.result?.name ||
    (jobId ? `scan_${jobId}.pdf` : `scan_${Date.now()}.pdf`);

  if (url) {
    const { data: blob } = await axios.get(url, {
      headers: scanHeaders(),
      responseType: "blob",
    });
    return new File([blob], fileName, { type: "application/pdf" });
  }

  // 2) base64 embebido
  const b64 =
    status?.result?.base64 ||
    status?.base64 ||
    status?.result?.dataBase64 ||
    status?.result?.pdfBase64;

  if (b64) {
    const cleanB64 = String(b64).replace(/^data:application\/pdf;base64,/, "");
    const bytes = Uint8Array.from(atob(cleanB64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], { type: "application/pdf" });
    return new File([blob], fileName, { type: "application/pdf" });
  }

  // 3) Fallback: descargar por jobId
  if (jobId) {
    const id = encodeURIComponent(jobId);

    const downloadCandidates = [
      `${SCAN_AGENT_BASE}/scan/download/${id}`,
      `${SCAN_AGENT_BASE}/scan/result/${id}`,
      `${SCAN_AGENT_BASE}/scan/${id}/download`,
      `${SCAN_AGENT_BASE}/jobs/${id}/download`,
      `${SCAN_AGENT_BASE}/jobs/${id}/result`,
      `${SCAN_AGENT_BASE}/download/${id}`,
      `${SCAN_AGENT_BASE}/result/${id}`,
      `${SCAN_AGENT_BASE}/files/${id}`,
      `${SCAN_AGENT_BASE}/file/${id}`,
    ];

    for (const u of downloadCandidates) {
      try {
        const res = await axios.get(u, {
          headers: scanHeaders(),
          responseType: "blob",
          validateStatus: () => true,
        });

        if (!(res.status >= 200 && res.status < 300)) continue;

        const blob = res.data;
        const size = blob?.size || 0;
        if (size < 1000) continue;

        const ct =
          res.headers?.["content-type"] ||
          res.headers?.["Content-Type"] ||
          "application/pdf";

        return new File([blob], fileName, {
          type: String(ct).includes("pdf")
            ? "application/pdf"
            : "application/octet-stream",
        });
      } catch {
        // sigue al siguiente endpoint
      }
    }
  }

  throw new Error(
    "El ScanAgent terminó pero no devolvió URL/base64 ni pude descargar por jobId. " +
      "Tu ScanAgent debe exponer un endpoint de descarga o incluir result.url."
  );
}

async function fetchDevices(driver = "wia") {
  const { data } = await axios.get(`${SCAN_AGENT_BASE}/devices`, {
    params: { driver },
    headers: scanHeaders(),
  });

  const list = Array.isArray(data) ? data : data?.devices || [];
  return list
    .map((d) => ({
      name: d?.name || d?.deviceName || (typeof d === "string" ? d : ""),
      id: d?.id || "",
    }))
    .filter((x) => String(x.name || "").trim().length > 0);
}

async function scanToPdfFile({
  driver = "wia",
  dpi = 300,
  source = "feeder",
  duplex = false,
  removeBlanks = true,
  blankThreshold = 0.985,
  deviceName = null,
  pageSize = "auto",
  pageSizeCustom = null,
} = {}) {
  const { data: start } = await axios.post(
    `${SCAN_AGENT_BASE}/scan`,
    {
      driver,
      dpi,
      source,
      duplex,
      removeBlanks,
      blankThreshold,
      deviceName,
      pageSize,
      pageSizeCustom,
    },
    { headers: { ...scanHeaders(), "Content-Type": "application/json" } }
  );

  const finalStatus = await pollScanStatusCompat(start);
  return await buildFileFromStatus(finalStatus, start);
}

/* =========================
   Opciones SELECT
========================= */
const PROJECT_OPTIONS = [
  "LICENCIAS DE CONDUCIR",
  "EMPLACAMIENTO",
  "OME CHETUMAL PASAPORTES",
  "DOCUMENTACION DE LA EMPRESA",
  "DOCUMENTACION DE EMPLEADOS",
  "OTROS",
];

const DOC_TYPE_OPTIONS = [
  "IDENTIFICACION",
  "FACTURA",
  "CONTRATO",
  "OFICIO",
  "EVIDENCIA",
  "OTROS",
];

const PAGE_SIZE_OPTIONS = [
  { value: "auto", label: "Auto Detect" },
  { value: "letter", label: "Carta (8.5 × 11)" },
  { value: "mx_oficio", label: "Oficio MX (8.5 × 13)" },
  { value: "legal", label: "Legal (8.5 × 14)" },
  { value: "a4", label: "A4 (210 × 297 mm)" },
  { value: "custom", label: "Personalizado" },
];

/* =========================
   Helpers
========================= */
function bytesToSize(bytes = 0) {
  const units = ["B", "KB", "MB", "GB"];
  let v = Number(bytes) || 0;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

function isAllowed(file) {
  const allowed = ["application/pdf", "image/jpeg", "image/png", "image/webp"];
  return allowed.includes(file.type);
}

function badgeForType(mime) {
  if (mime === "application/pdf")
    return { label: "PDF", cls: "bg-red-50 text-red-700 ring-red-200" };
  if (mime?.startsWith("image/"))
    return { label: "IMG", cls: "bg-blue-50 text-blue-700 ring-blue-200" };
  return { label: "FILE", cls: "bg-gray-50 text-gray-700 ring-gray-200" };
}

function normalizeError(err) {
  if (!err) return "Error desconocido";

  if (err.name === "CanceledError") return "Subida cancelada.";
  if (err.code === "ERR_CANCELED") return "Subida cancelada.";
  if (err instanceof DOMException && err.name === "AbortError")
    return "Subida cancelada.";

  if (err.response) {
    const status = err.response.status;
    const msg =
      err.response.data?.message ||
      err.response.data?.error ||
      err.response.data?.msg ||
      JSON.stringify(err.response.data || {});
    return `HTTP ${status}: ${msg}`;
  }
  if (err.message) return err.message;
  return String(err);
}

function clean(v) {
  return String(v ?? "").trim();
}

function normalizeBatchKey(value = "") {
  return clean(value)
    .toUpperCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function baseName(fileName = "") {
  return String(fileName).replace(/\.[^/.]+$/, "");
}

function hasDocName(it) {
  return clean(it?.documentName).length >= 3;
}

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

/* =========================
   Tiny Toasts (sin libs)
========================= */
function Toasts({ toasts, onClose }) {
  return (
    <div className="fixed right-4 top-4 z-[80] flex w-[360px] max-w-[calc(100vw-2rem)] flex-col gap-2">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={cx(
            "rounded-2xl p-3 shadow-lg ring-1 bg-white",
            t.type === "success" && "ring-emerald-200",
            t.type === "error" && "ring-rose-200",
            t.type === "info" && "ring-slate-200"
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">
                {t.type === "success"
                  ? "OK"
                  : t.type === "error"
                  ? "Error"
                  : "Info"}
              </div>
              <div className="mt-0.5 break-words text-sm text-slate-700 whitespace-pre-wrap">
                {t.message}
              </div>
            </div>
            <button
              onClick={() => onClose(t.id)}
              className="rounded-xl px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              title="Cerrar"
            >
              ✕
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Chip({ tone = "neutral", children }) {
  const map = {
    neutral: UI.chipNeutral,
    ok: UI.chipOk,
    err: UI.chipErr,
    info: UI.chipInfo,
    warn: UI.chipWarn,
  };
  return (
    <span
      className={cx(
        "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold",
        map[tone] || map.neutral
      )}
    >
      {children}
    </span>
  );
}

function Btn({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={cx(
        "rounded-xl px-4 py-2 text-sm font-semibold transition",
        "disabled:cursor-not-allowed disabled:opacity-50 active:scale-[0.99]",
        className
      )}
    >
      {children}
    </button>
  );
}

function ProgressBar({ value }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div className={cx("h-2 w-full overflow-hidden rounded-full", UI.progressTrack)}>
      <div
        className={cx("h-2 rounded-full transition-all", UI.progressBar)}
        style={{ width: `${v}%` }}
      />
    </div>
  );
}

/* =========================
   Scan Modal (Enterprise Simple)
========================= */
function ScanModal({
  open,
  onClose,
  scanning,
  totalsUploading,
  contextError,

  scanDriver,
  setScanDriver,
  devices,
  deviceLoading,
  deviceError,
  selectedDeviceName,
  setSelectedDeviceName,
  refreshDevices,

  scanSource,
  setScanSource,
  scanDuplex,
  setScanDuplex,
  scanDpi,
  setScanDpi,
  scanRemoveBlanks,
  setScanRemoveBlanks,

  scanPageSize,
  setScanPageSize,
  scanPageSizeCustom,
  setScanPageSizeCustom,

  onScan,
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  if (!open) return null;

  const disabled = scanning || totalsUploading > 0;

  const pageLabel =
    scanPageSize === "custom"
      ? `Custom ${scanPageSizeCustom?.wIn ?? "?"}×${scanPageSizeCustom?.hIn ?? "?"} in`
      : PAGE_SIZE_OPTIONS.find((x) => x.value === scanPageSize)?.label || "Auto";

  const summary = [
    scanSource === "flatbed" ? "Cama" : "ADF",
    scanSource === "flatbed" ? null : scanDuplex ? "Dúplex" : "Una cara",
    `${scanDpi || 300} DPI`,
    pageLabel,
    scanRemoveBlanks ? "Quita blancas" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-slate-200">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-6 py-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-lg font-semibold text-slate-900">
                Escanear documentos
              </div>

              <span
                className={cx(
                  "inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1",
                  devices?.length
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                    : "bg-slate-50 text-slate-700 ring-slate-200"
                )}
              >
                {devices?.length ? "Escáner detectado" : "Sin escáner"}
              </span>
            </div>

            <div className="mt-1 text-sm text-slate-600">
              {selectedDeviceName ? (
                <>
                  <span className="font-semibold text-slate-900">
                    {selectedDeviceName}
                  </span>{" "}
                  <span className="text-slate-400">·</span>{" "}
                  <span className="text-slate-700">{summary}</span>
                </>
              ) : (
                <span className="text-slate-700">{summary}</span>
              )}
            </div>
          </div>

          <button
            onClick={onClose}
            className="rounded-2xl px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
            title="Cerrar"
          >
            ✕
          </button>
        </div>

        <div className="px-6 py-5">
          {contextError && (
            <div className="mb-4 flex items-start gap-3 rounded-2xl bg-amber-50 p-3 ring-1 ring-amber-100">
              <div className="grid h-8 w-8 place-items-center rounded-xl bg-amber-100 text-amber-900">
                !
              </div>
              <div className="min-w-0">
                <div className="text-xs font-semibold text-amber-900">
                  Falta contexto
                </div>
                <div className="text-sm text-amber-900/90">{contextError}</div>
              </div>
            </div>
          )}

          {deviceError && (
            <div className="mb-4 rounded-2xl bg-rose-50 p-3 text-xs font-semibold text-rose-700 ring-1 ring-rose-100">
              Error listando escáneres: {deviceError}
            </div>
          )}

          <div className="rounded-2xl bg-white p-4 ring-1 ring-slate-200">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-slate-900">Opciones</div>
                <div className="text-xs text-slate-500">
                  Ajustes recomendados para escaneo rápido
                </div>
              </div>

              <button
                type="button"
                onClick={() => setAdvancedOpen((v) => !v)}
                className="rounded-xl px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
              >
                {advancedOpen ? "Ocultar avanzado" : "Avanzado"}
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
              <div className="md:col-span-4">
                <label className={UI.label}>Fuente</label>
                <select
                  value={scanSource}
                  onChange={(e) => {
                    const v = e.target.value;
                    setScanSource(v);
                    if (v === "flatbed") setScanDuplex(false);
                  }}
                  className={cx("mt-1", UI.select)}
                  disabled={disabled}
                >
                  <option value="feeder">ADF (alimentador)</option>
                  <option value="flatbed">Cama (flatbed)</option>
                </select>
              </div>

              <div className="md:col-span-4">
                <label className={UI.label}>Modo</label>
                <div className="mt-1 grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setScanDuplex(false)}
                    disabled={disabled}
                    className={cx(
                      "rounded-xl border px-3 py-2 text-sm font-semibold transition",
                      !scanDuplex
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50"
                    )}
                  >
                    Una cara
                  </button>
                  <button
                    type="button"
                    onClick={() => setScanDuplex(true)}
                    disabled={disabled || scanSource === "flatbed"}
                    className={cx(
                      "rounded-xl border px-3 py-2 text-sm font-semibold transition",
                      scanDuplex
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-800 hover:bg-slate-50",
                      scanSource === "flatbed" && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    Doble cara
                  </button>
                </div>
                {scanSource === "flatbed" && (
                  <div className="mt-1 text-xs text-slate-500">
                    Dúplex no aplica en cama.
                  </div>
                )}
              </div>

              <div className="md:col-span-4">
                <label className={UI.label}>Calidad</label>
                <select
                  value={scanDpi}
                  onChange={(e) => setScanDpi(Number(e.target.value))}
                  className={cx("mt-1", UI.select)}
                  disabled={disabled}
                >
                  <option value={200}>200 DPI</option>
                  <option value={300}>300 DPI (recomendado)</option>
                  <option value={400}>400 DPI</option>
                </select>
              </div>

              <div className="md:col-span-8">
                <label className={UI.label}>Tamaño de hoja</label>
                <select
                  value={scanPageSize}
                  onChange={(e) => setScanPageSize(e.target.value)}
                  className={cx("mt-1", UI.select)}
                  disabled={disabled}
                >
                  {PAGE_SIZE_OPTIONS.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-4">
                <label className={UI.label}>Limpieza</label>
                <label className="mt-1 flex items-center justify-between rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                  <span>Quitar blancas</span>
                  <input
                    type="checkbox"
                    checked={scanRemoveBlanks}
                    onChange={(e) => setScanRemoveBlanks(e.target.checked)}
                    disabled={disabled}
                  />
                </label>
              </div>

              {scanPageSize === "custom" && (
                <>
                  <div className="md:col-span-4">
                    <label className={UI.label}>Ancho (in)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={scanPageSizeCustom.wIn}
                      onChange={(e) =>
                        setScanPageSizeCustom((p) => ({
                          ...p,
                          wIn: Number(e.target.value) || 0,
                        }))
                      }
                      className={cx("mt-1", UI.input)}
                      disabled={disabled}
                    />
                  </div>

                  <div className="md:col-span-4">
                    <label className={UI.label}>Alto (in)</label>
                    <input
                      type="number"
                      step="0.1"
                      value={scanPageSizeCustom.hIn}
                      onChange={(e) =>
                        setScanPageSizeCustom((p) => ({
                          ...p,
                          hIn: Number(e.target.value) || 0,
                        }))
                      }
                      className={cx("mt-1", UI.input)}
                      disabled={disabled}
                    />
                  </div>

                  <div className="md:col-span-4 flex items-end">
                    <div className="w-full rounded-xl bg-slate-50 p-3 text-xs text-slate-700 ring-1 ring-slate-200">
                      Custom: {scanPageSizeCustom.wIn} × {scanPageSizeCustom.hIn} in
                    </div>
                  </div>
                </>
              )}
            </div>

            {advancedOpen && (
              <div className="mt-4 rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">
                      Avanzado
                    </div>
                    <div className="text-xs text-slate-500">Driver y escáner</div>
                  </div>

                  <Btn
                    onClick={refreshDevices}
                    disabled={disabled || deviceLoading}
                    className={cx(UI.btnSecondary, "px-3 py-2 text-xs")}
                  >
                    {deviceLoading ? "..." : "Refrescar"}
                  </Btn>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
                  <div className="md:col-span-4">
                    <label className={UI.label}>Driver</label>
                    <select
                      value={scanDriver}
                      onChange={(e) => setScanDriver(e.target.value)}
                      className={cx("mt-1", UI.select)}
                      disabled={disabled}
                    >
                      <option value="wia">WIA</option>
                      <option value="twain">TWAIN</option>
                    </select>
                  </div>

                  <div className="md:col-span-8">
                    <label className={UI.label}>Escáner</label>
                    <select
                      value={selectedDeviceName}
                      onChange={(e) => setSelectedDeviceName(e.target.value)}
                      className={cx("mt-1", UI.select)}
                      disabled={disabled || deviceLoading || devices.length === 0}
                    >
                      {devices.length === 0 ? (
                        <option value="">
                          {deviceLoading
                            ? "Cargando..."
                            : "No se detectaron escáneres"}
                        </option>
                      ) : (
                        devices.map((d) => (
                          <option key={d.name} value={d.name}>
                            {d.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-slate-100 bg-white px-6 py-4 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <div className="text-xs text-slate-500">
              Se generará un PDF y se agregará a la cola.
            </div>
            <div className="truncate text-sm font-semibold text-slate-900">
              {summary}
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Btn
              onClick={onClose}
              className={UI.btnSecondary}
              disabled={scanning}
            >
              Cancelar
            </Btn>

            <Btn
              onClick={onScan}
              disabled={scanning || !!contextError || totalsUploading > 0}
              className="bg-slate-900 text-white hover:bg-black shadow-sm"
              title={contextError || ""}
            >
              {scanning ? "Escaneando…" : "Escanear ahora"}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================
   Concurrency runner
========================= */
async function runWithConcurrency(taskFns, limit, onSettled) {
  const q = [...taskFns];
  const running = new Set();

  const launchNext = async () => {
    if (q.length === 0) return;

    const fn = q.shift();
    if (!fn) return;

    const p = Promise.resolve()
      .then(fn)
      .catch(() => {})
      .finally(() => {
        running.delete(p);
        onSettled?.();
      });

    running.add(p);

    if (running.size < limit && q.length > 0) {
      await launchNext();
    }
  };

  const starters = [];
  const initial = Math.min(limit, q.length);
  for (let i = 0; i < initial; i++) starters.push(launchNext());

  await Promise.all(starters);
  await Promise.all(Array.from(running));
}

/* =========================
   Scan Overlay PRO
========================= */
function ScanOverlay({ open }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md rounded-3xl p-8 shadow-2xl">
        <div className="flex items-center gap-4">
          <div className="relative h-14 w-14">
            <div className="absolute inset-0 rounded-full border-4 border-slate-200"></div>
            <div className="absolute inset-0 rounded-full border-4 border-slate-900 border-t-transparent animate-spin"></div>
          </div>

          <div>
            <div className="text-lg font-semibold text-slate-900">
              Escaneando documento…
            </div>
            <div className="text-sm text-slate-600">
              Procesando páginas y generando PDF.
            </div>
          </div>
        </div>

        <div className="mt-6 h-2 w-full bg-slate-100 rounded-full overflow-hidden">
          <div className="h-2 bg-slate-900 animate-pulse w-2/3 rounded-full"></div>
        </div>
      </div>
    </div>
  );
}

/* =========================
   Preview Modal PRO
========================= */
function PreviewModal({ file, onClose, onConfirm }) {
  if (!file) return null;

  const url = URL.createObjectURL(file);
  const isPdf = file.type === "application/pdf";

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/60 p-6">
      <div className="bg-white w-full max-w-6xl rounded-3xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div className="text-lg font-semibold text-slate-900">Vista previa</div>
          <button
            onClick={onClose}
            className="text-sm font-semibold text-slate-600 hover:bg-slate-50 px-3 py-2 rounded-xl"
          >
            ✕
          </button>
        </div>

        <div className="h-[75vh] bg-slate-100">
          {isPdf ? (
            <iframe src={url} className="w-full h-full" title="preview" />
          ) : (
            <img src={url} alt="preview" className="w-full h-full object-contain" />
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-100">
          <Btn onClick={onClose} className={UI.btnSecondary}>
            Cancelar
          </Btn>
          <Btn onClick={onConfirm} className="bg-slate-900 text-white hover:bg-black">
            Agregar a la cola
          </Btn>
        </div>
      </div>
    </div>
  );
}

/* =========================
   MAIN (DEFAULT EXPORT)
========================= */
export default function UploadDocumentos() {
  const inputRef = useRef(null);

  // ✅ configurable
  const [concurrency, setConcurrency] = useState(4);

  // Scan state
  const [scanning, setScanning] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  // Modal
  const [showScanSettings, setShowScanSettings] = useState(false);

  // Scan settings
  const [scanDriver, setScanDriver] = useState("wia");
  const [devices, setDevices] = useState([]);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [deviceError, setDeviceError] = useState("");
  const [selectedDeviceName, setSelectedDeviceName] = useState("");

  const [scanSource, setScanSource] = useState("feeder");
  const [scanDuplex, setScanDuplex] = useState(false);
  const [scanDpi, setScanDpi] = useState(300);
  const [scanRemoveBlanks, setScanRemoveBlanks] = useState(true);

  const [scanPageSize, setScanPageSize] = useState("auto");
  const [scanPageSizeCustom, setScanPageSizeCustom] = useState({
    wIn: 8.5,
    hIn: 11,
  });

  // Contexto
  const [projectSel, setProjectSel] = useState("LICENCIAS DE CONDUCIR");
  const [projectOther, setProjectOther] = useState("");
  const [projectNameManual, setProjectNameManual] = useState("");
  const [applyLotNameToAll, setApplyLotNameToAll] = useState(true);

  const [docSel, setDocSel] = useState("IDENTIFICACION");
  const [docOther, setDocOther] = useState("");

  // Drag/drop
  const [dragOver, setDragOver] = useState(false);

  // Toasts
  const [toasts, setToasts] = useState([]);
  const toast = (type, message, ms = 3500) => {
    const id =
      (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
      `${Date.now()}_${Math.random()}`;
    setToasts((prev) => [...prev, { id, type, message }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, ms);
  };

  // Upload queue
  const [items, setItems] = useState([]);

  const expedienteId = useMemo(() => {
    if (projectSel === "OTROS") return clean(projectOther);
    return clean(projectSel);
  }, [projectSel, projectOther]);

  const projectDisplayName = useMemo(
    () => clean(projectNameManual),
    [projectNameManual]
  );

  const tipo = useMemo(() => {
    if (docSel === "OTROS") return clean(docOther);
    return clean(docSel);
  }, [docSel, docOther]);

  const contextError = useMemo(() => {
    if (!expedienteId) return "Selecciona Proyecto/Expediente.";
    if (projectSel === "OTROS" && !clean(projectOther))
      return "Escribe el Proyecto/Expediente (OTROS).";
    if (!projectDisplayName) return "Escribe el Nombre del lote (obligatorio).";
    if (!tipo) return "Selecciona Tipo de documento.";
    if (docSel === "OTROS" && !clean(docOther))
      return "Escribe el Tipo de documento (OTROS).";
    return null;
  }, [
    expedienteId,
    projectSel,
    projectOther,
    projectDisplayName,
    tipo,
    docSel,
    docOther,
  ]);

  const hasFiles = items.length > 0;

  const totals = useMemo(() => {
    const total = items.length;
    const done = items.filter((x) => x.status === "done").length;
    const uploading = items.filter((x) => x.status === "uploading").length;
    const error = items.filter((x) => x.status === "error").length;
    const ready = items.filter((x) => x.status === "ready").length;
    const canceled = items.filter((x) => x.status === "canceled").length;

    const avg =
      total === 0
        ? 0
        : Math.round(items.reduce((a, x) => a + (x.progress || 0), 0) / total);

    return { total, done, uploading, error, ready, canceled, avg };
  }, [items]);

  const canUploadAll = useMemo(() => {
    const pending = items.filter((x) =>
      ["ready", "error", "canceled"].includes(x.status)
    );
    const allNamed = pending.every((x) => hasDocName(x));
    return hasFiles && totals.uploading === 0 && !contextError && allNamed;
  }, [hasFiles, totals.uploading, contextError, items]);

  // auto-apply lot name if not touched
  useEffect(() => {
    if (!applyLotNameToAll) return;
    const lotName = clean(projectNameManual);
    if (lotName.length < 3) return;

    setItems((prev) =>
      prev.map((x) => {
        if (x.status === "done" || x.status === "uploading") return x;
        if (x.nameTouched) return x;
        return { ...x, documentName: lotName };
      })
    );
  }, [projectNameManual, applyLotNameToAll]);

  // devices load
  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        setDeviceLoading(true);
        setDeviceError("");
        const list = await fetchDevices(scanDriver);
        if (!mounted) return;

        setDevices(list);
        if (!selectedDeviceName && list.length > 0)
          setSelectedDeviceName(list[0].name);
        if (list.length === 0) setSelectedDeviceName("");
      } catch (e) {
        if (!mounted) return;
        setDevices([]);
        setSelectedDeviceName("");
        setDeviceError(normalizeError(e));
      } finally {
        if (mounted) setDeviceLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanDriver]);

  const refreshDevicesUI = async () => {
    try {
      setDeviceLoading(true);
      setDeviceError("");
      const list = await fetchDevices(scanDriver);
      setDevices(list);
      if (!selectedDeviceName && list.length > 0)
        setSelectedDeviceName(list[0].name);
      toast("success", `Escáneres detectados: ${list.length}`);
    } catch (e) {
      setDeviceError(normalizeError(e));
      toast("error", `No pude listar escáneres: ${normalizeError(e)}`);
    } finally {
      setDeviceLoading(false);
    }
  };

  const pickFiles = () => inputRef.current?.click();

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    const valid = [];
    const invalid = [];

    files.forEach((f) => (isAllowed(f) ? valid.push(f) : invalid.push(f)));

    if (invalid.length) {
      toast(
        "error",
        `Archivos no permitidos:\n${invalid
          .map((f) => `- ${f.name} (${f.type || "sin tipo"})`)
          .join("\n")}`
      );
    }

    const lotName = clean(projectNameManual);
    const canApplyLot = applyLotNameToAll && lotName.length >= 3;

    const mapped = valid.map((file) => ({
      id:
        (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
        `${Date.now()}_${Math.random()}`,
      file,
      status: "ready",
      progress: 0,
      key: null,
      documentId: null,
      error: null,
      controller: null,
      documentName: canApplyLot ? lotName : baseName(file.name),
      nameTouched: false,
    }));

    setItems((prev) => [...mapped, ...prev]);
    if (valid.length) toast("success", `${valid.length} archivo(s) agregado(s).`);
  };

  const handleScan = async () => {
    if (scanning) return;
    if (contextError) return toast("error", contextError);

    try {
      setScanning(true);

      const source = scanSource === "flatbed" ? "flatbed" : "feeder";
      const duplex = source === "feeder" ? !!scanDuplex : false;

      const pdfFile = await scanToPdfFile({
        driver: scanDriver,
        dpi: Number(scanDpi) || 300,
        source,
        duplex,
        removeBlanks: !!scanRemoveBlanks,
        blankThreshold: 0.985,
        deviceName: selectedDeviceName || null,
        pageSize: scanPageSize,
        pageSizeCustom: scanPageSize === "custom" ? scanPageSizeCustom : null,
      });

      setPreviewFile(pdfFile);
      setShowScanSettings(false);
    } catch (err) {
      toast("error", `Escaneo falló: ${normalizeError(err)}`);
    } finally {
      setScanning(false);
    }
  };

  const removeItem = (id) => {
    setItems((prev) => {
      const it = prev.find((x) => x.id === id);
      if (it?.controller) {
        try {
          it.controller.abort();
        } catch {}
      }
      return prev.filter((x) => x.id !== id);
    });
  };

  const clearAll = () => {
    setItems((prev) => {
      prev.forEach((it) => {
        if (it.controller) {
          try {
            it.controller.abort();
          } catch {}
        }
      });
      return [];
    });
    toast("info", "Lista limpiada.");
  };

  const cancelUpload = (id) => {
    setItems((prev) =>
      prev.map((x) => {
        if (x.id !== id) return x;
        if (x.controller) {
          try {
            x.controller.abort();
          } catch {}
        }
        return {
          ...x,
          status: "canceled",
          error: "Cancelado por el usuario.",
          controller: null,
        };
      })
    );
    toast("info", "Subida cancelada.");
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  const onBrowseChange = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };

  const uploadOne = async (itemId) => {
    const current = items.find((x) => x.id === itemId);
    if (!current) return;

    if (contextError) return toast("error", contextError);

    if (!hasDocName(current)) {
      setItems((prev) =>
        prev.map((x) =>
          x.id === itemId
            ? { ...x, status: "error", error: "Falta Nombre del documento (obligatorio)." }
            : x
        )
      );
      return toast(
        "error",
        `Falta Nombre del documento en: ${current.file?.name || "archivo"}`
      );
    }

    if (current.status === "done" || current.status === "uploading") return;

    const controller = new AbortController();

    setItems((prev) =>
      prev.map((x) =>
        x.id === itemId
          ? {
              ...x,
              status: "uploading",
              error: null,
              progress: 0,
              key: null,
              documentId: null,
              controller,
            }
          : x
      )
    );

    try {
      const file = current.file;

      const { data } = await getUploadUrlDocs({
        fileName: file.name,
        contentType: file.type,
        size: file.size,
        expedienteId,
        tipo,
        projectName: expedienteId,
        projectDisplayName,
        docType: tipo,
        batchName: projectDisplayName,
        batchKey: normalizeBatchKey(projectDisplayName),
      });

      if (!data?.uploadUrl || !data?.documentId || !(data?.s3Key || data?.key)) {
        throw new Error("Respuesta incompleta: falta uploadUrl/documentId/s3Key");
      }

      const s3Key = data.s3Key || data.key;

      await axios.put(data.uploadUrl, file, {
        headers: { "Content-Type": file.type },
        signal: controller.signal,
        onUploadProgress: (p) => {
          const pct = p.total ? Math.round((p.loaded * 100) / p.total) : 0;
          setItems((prev) =>
            prev.map((x) => (x.id === itemId ? { ...x, progress: pct } : x))
          );
        },
      });

      await confirmUploadDocs({
        documentId: data.documentId,
        projectName: data.projectName || expedienteId,
        projectDisplayName,
        docType: data.docType || tipo,
        batchName: projectDisplayName,
        batchKey: normalizeBatchKey(projectDisplayName),
        fileName: file.name,
        contentType: file.type,
        s3Key,
        sizeBytes: file.size,
        documentName: clean(current.documentName),
      });

      setItems((prev) =>
        prev.map((x) =>
          x.id === itemId
            ? {
                ...x,
                status: "done",
                progress: 100,
                key: s3Key,
                documentId: data.documentId,
                controller: null,
              }
            : x
        )
      );

      toast("success", `Subido: ${file.name}`);
    } catch (err) {
      const msg = normalizeError(err);
      const canceled = msg.toLowerCase().includes("cancel");

      setItems((prev) =>
        prev.map((x) =>
          x.id === itemId
            ? {
                ...x,
                status: canceled ? "canceled" : "error",
                error: msg,
                controller: null,
              }
            : x
        )
      );

      if (!canceled) toast("error", `${current.file?.name || "Archivo"}: ${msg}`);
    }
  };

  const uploadAllParallel = async () => {
    if (contextError) return toast("error", contextError);

    const pending = items.filter((x) =>
      ["ready", "error", "canceled"].includes(x.status)
    );
    if (pending.length === 0) return toast("info", "No hay archivos pendientes.");

    const missing = pending.filter((x) => !hasDocName(x));
    if (missing.length > 0) {
      toast(
        "error",
        `Faltan nombres en ${missing.length} archivo(s). Completa “Nombre del documento”.`
      );
      setItems((prev) =>
        prev.map((x) => {
          if (!["ready", "error", "canceled"].includes(x.status)) return x;
          if (hasDocName(x)) return x;
          return { ...x, status: "error", error: "Falta Nombre del documento (obligatorio)." };
        })
      );
      return;
    }

    toast("info", `Subiendo en paralelo (${concurrency})…`);

    const ids = pending.map((x) => x.id);
    const taskFns = ids.map((id) => () => uploadOne(id));
    await runWithConcurrency(taskFns, Math.max(1, Number(concurrency) || 1));

    toast("success", "Subida finalizada.");
  };

  const retryErrors = async () => {
    if (contextError) return toast("error", contextError);

    const retryList = items.filter((x) => x.status === "error" || x.status === "canceled");
    if (retryList.length === 0) return toast("info", "No hay errores para reintentar.");

    const missing = retryList.filter((x) => !hasDocName(x));
    if (missing.length > 0) {
      toast(
        "error",
        `Hay ${missing.length} archivo(s) sin Nombre del documento. Completa el campo y reintenta.`
      );
      setItems((prev) =>
        prev.map((x) => {
          if (x.status !== "error" && x.status !== "canceled") return x;
          if (hasDocName(x)) return x;
          return { ...x, status: "error", error: "Falta Nombre del documento (obligatorio)." };
        })
      );
      return;
    }

    const taskFns = retryList.map((x) => () => uploadOne(x.id));
    await runWithConcurrency(taskFns, Math.max(1, Number(concurrency) || 1));

    toast("success", "Reintento finalizado.");
  };

  const headerChips = (
    <div className="flex flex-wrap items-center gap-2">
      <Chip tone="info">Proyecto: {expedienteId || "—"}</Chip>
      <Chip tone="neutral">Tipo: {tipo || "—"}</Chip>
      {totals.total > 0 ? (
        <>
          <Chip tone="neutral">{totals.total} en cola</Chip>
          {totals.uploading > 0 && <Chip tone="info">{totals.uploading} subiendo</Chip>}
          {totals.done > 0 && <Chip tone="ok">{totals.done} OK</Chip>}
          {totals.error > 0 && <Chip tone="err">{totals.error} errores</Chip>}
        </>
      ) : (
        <Chip tone="neutral">Sin archivos</Chip>
      )}
    </div>
  );

  return (
    <div className={`min-h-screen ${UI.page}`}>
      <Toasts
        toasts={toasts}
        onClose={(id) => setToasts((p) => p.filter((x) => x.id !== id))}
      />

      <ScanOverlay open={scanning} />

      <PreviewModal
        file={previewFile}
        onClose={() => setPreviewFile(null)}
        onConfirm={() => {
          addFiles([previewFile]);
          setPreviewFile(null);
          toast("success", "Documento agregado a la cola.");
        }}
      />

      <ScanModal
        open={showScanSettings}
        onClose={() => setShowScanSettings(false)}
        scanning={scanning}
        totalsUploading={totals.uploading}
        contextError={contextError}
        scanDriver={scanDriver}
        setScanDriver={setScanDriver}
        devices={devices}
        deviceLoading={deviceLoading}
        deviceError={deviceError}
        selectedDeviceName={selectedDeviceName}
        setSelectedDeviceName={setSelectedDeviceName}
        refreshDevices={refreshDevicesUI}
        scanSource={scanSource}
        setScanSource={setScanSource}
        scanDuplex={scanDuplex}
        setScanDuplex={setScanDuplex}
        scanDpi={scanDpi}
        setScanDpi={setScanDpi}
        scanRemoveBlanks={scanRemoveBlanks}
        setScanRemoveBlanks={setScanRemoveBlanks}
        scanPageSize={scanPageSize}
        setScanPageSize={setScanPageSize}
        scanPageSizeCustom={scanPageSizeCustom}
        setScanPageSizeCustom={setScanPageSizeCustom}
        onScan={handleScan}
      />

      <div className="mx-auto max-w-6xl px-4 pt-8 pb-28">
        {/* Header */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              Subir documentos
            </h1>
            <p className="mt-1 text-sm text-slate-600"></p>
          </div>
          {headerChips}
        </div>

        {/* Context card */}
        <div className={`${UI.card} p-5`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm font-semibold text-slate-900"></div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Concurrencia</span>
              <select
                value={concurrency}
                onChange={(e) => setConcurrency(Number(e.target.value))}
                className={cx("w-[84px]", UI.select)}
              >
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-12 md:items-start">
            <div className="md:col-span-4">
              <label className={UI.label}>Nombre de lote de documentos *</label>
              <input
                value={projectNameManual}
                onChange={(e) => setProjectNameManual(e.target.value)}
                placeholder="Ej. Licencias Playa del Carmen 2026"
                className={cx("mt-2", UI.input)}
              />

              <label className="mt-3 flex items-center gap-2 text-xs text-slate-600 select-none">
                <input
                  type="checkbox"
                  checked={applyLotNameToAll}
                  onChange={(e) => setApplyLotNameToAll(e.target.checked)}
                />
                Aplicar a archivos nuevos (si no fueron editados)
              </label>
            </div>

            <div className="md:col-span-4">
              <label className={UI.label}>Proyecto / Expediente (clave)</label>
              <select
                value={projectSel}
                onChange={(e) => {
                  const v = e.target.value;
                  setProjectSel(v);
                  if (v !== "OTROS") setProjectOther("");
                }}
                className={cx("mt-2", UI.select)}
              >
                {PROJECT_OPTIONS.map((op) => (
                  <option key={op} value={op}>
                    {op}
                  </option>
                ))}
              </select>

              {projectSel === "OTROS" && (
                <input
                  value={projectOther}
                  onChange={(e) => setProjectOther(e.target.value)}
                  placeholder="Especifica el proyecto clave…"
                  className={cx("mt-2", UI.input)}
                />
              )}
            </div>

            <div className="md:col-span-4">
              <label className={UI.label}>Tipo de documento</label>
              <select
                value={docSel}
                onChange={(e) => {
                  const v = e.target.value;
                  setDocSel(v);
                  if (v !== "OTROS") setDocOther("");
                }}
                className={cx("mt-2", UI.select)}
              >
                {DOC_TYPE_OPTIONS.map((op) => (
                  <option key={op} value={op}>
                    {op}
                  </option>
                ))}
              </select>

              {docSel === "OTROS" && (
                <input
                  value={docOther}
                  onChange={(e) => setDocOther(e.target.value)}
                  placeholder="Especifica el tipo de documento…"
                  className={cx("mt-2", UI.input)}
                />
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <Chip tone="neutral">
              Clave: <span className="font-mono">{expedienteId || "—"}</span>
            </Chip>
            <Chip tone="neutral">
              Nombre: <span className="font-mono">{projectDisplayName || "—"}</span>
            </Chip>
            <Chip tone="neutral">
              Tipo: <span className="font-mono">{tipo || "—"}</span>
            </Chip>
            {contextError && <Chip tone="warn">{contextError}</Chip>}
          </div>
        </div>

        {/* Main */}
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* Dropzone Gobierno Digital (sobrio + claro) */}
<div className="lg:col-span-7">
  <div
    onDragEnter={(e) => {
      e.preventDefault();
      setDragOver(true);
    }}
    onDragOver={(e) => e.preventDefault()}
    onDragLeave={() => setDragOver(false)}
    onDrop={onDrop}
    className={cx(UI.card, "overflow-hidden")}
  >
    {/* Barra institucional */}
    <div className="h-1 bg-slate-900" />

    {/* Header */}
    <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
      <div className="flex items-center gap-3 min-w-0">
        <div className="grid h-9 w-9 place-items-center rounded-xl bg-slate-900 text-white">
          <span className="text-sm leading-none">↑</span>
        </div>

        <div className="min-w-0">
          <div className="text-base font-semibold text-slate-900">
            Carga de documentos
          </div>
          <div
            className={cx(
              "mt-0.5 text-xs font-semibold",
              contextError ? "text-amber-700" : "text-emerald-700"
            )}
          >
            {contextError ? "Contexto incompleto" : "Contexto listo"}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Chip tone="neutral">{totals.ready} listos</Chip>
        {totals.error > 0 && <Chip tone="err">{totals.error} errores</Chip>}
      </div>
    </div>

    {/* Body */}
    <div className="px-6 py-6">
      <div
        className={cx(
          "rounded-2xl border p-8 transition",
          dragOver ? "border-slate-400 bg-slate-50" : "border-slate-200 bg-white"
        )}
      >
        <div className="flex flex-col items-center text-center">
          {/* Icono sobrio */}
          <div className="grid h-14 w-14 place-items-center rounded-2xl bg-slate-900 text-white">
            <span className="text-lg leading-none">↑</span>
          </div>

          <div className="mt-4 text-sm font-semibold text-slate-900">
            Arrastra archivos aquí
          </div>

          <div className="mt-1 text-xs text-slate-600">
            PDF o imágenes · Máx {process.env.REACT_APP_DOCS_MAX_MB || 15}MB
          </div>

          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <Btn
              onClick={pickFiles}
              disabled={totals.uploading > 0}
              className={cx(UI.btnPrimary, "px-5 py-2.5")}
            >
              Seleccionar archivos
            </Btn>

            <Btn
              onClick={() => setShowScanSettings(true)}
              disabled={!!contextError || totals.uploading > 0}
              className={cx(UI.btnSecondary, "px-5 py-2.5")}
              title={contextError || ""}
            >
              Escanear
            </Btn>
          </div>

          {/* Solo si falta contexto: 1 línea, concreta */}
          {contextError && (
            <div className="mt-4 text-xs text-slate-600">
              Completa <span className="font-semibold">Nombre de lote</span> y{" "}
              <span className="font-semibold">Tipo</span> para habilitar acciones.
            </div>
          )}
        </div>

        {/* Progreso compacto */}
        {totals.uploading > 0 && (
          <div className="mt-6 border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between">
              <div className="text-xs font-semibold text-slate-700">
                Cargando…
              </div>
              <div className="text-xs text-slate-500 tabular-nums">
                {totals.avg}%
              </div>
            </div>
            <div className="mt-2">
              <ProgressBar value={totals.avg} />
            </div>
          </div>
        )}
      </div>

      {/* input real oculto */}
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/*"
        multiple
        onChange={onBrowseChange}
        className="hidden"
      />
    </div>
  </div>
</div>

          {/* Queue */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-base font-semibold text-slate-900">Cola de subida</div>
                  
                </div>
                <Chip tone="neutral">{items.length} archivos</Chip>
              </div>

              {!hasFiles ? (
                <div className="mt-4 rounded-2xl bg-slate-50 p-6 text-center text-sm text-slate-600 ring-1 ring-slate-200">
                  No hay archivos aún. Agrega un PDF o una imagen.
                </div>
              ) : (
                <div className="mt-4 space-y-3">
                  {items.map((it) => {
                    const { label, cls } = badgeForType(it.file.type);
                    const docNameOk = hasDocName(it);

                    const showUploadBtn =
                      it.status === "ready" ||
                      it.status === "error" ||
                      it.status === "canceled";
                    const showCancelBtn = it.status === "uploading";

                    const statusTone =
                      it.status === "done"
                        ? "ok"
                        : it.status === "error"
                        ? "err"
                        : it.status === "uploading"
                        ? "info"
                        : it.status === "canceled"
                        ? "warn"
                        : "neutral";

                    return (
                      <div
                        key={it.id}
                        className={cx(
                          "rounded-2xl border p-4",
                          it.status === "error"
                            ? "border-rose-200 bg-rose-50"
                            : "border-slate-200 bg-white"
                        )}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span
                                className={cx(
                                  "inline-flex items-center rounded-lg px-2 py-1 text-xs font-semibold ring-1",
                                  cls
                                )}
                              >
                                {label}
                              </span>
                              <Chip tone={statusTone}>{it.status}</Chip>
                              <span className="text-xs text-slate-500">
                                {bytesToSize(it.file.size)}
                              </span>
                            </div>

                            <div className="mt-2 text-sm font-semibold text-slate-900 break-words">
                              {it.file.name}
                            </div>

                            <div className="mt-3">
                              <label className={UI.label}>Nombre del documento *</label>
                              <input
                                value={it.documentName || ""}
                                onChange={(e) =>
                                  setItems((prev) =>
                                    prev.map((x) =>
                                      x.id === it.id
                                        ? { ...x, documentName: e.target.value, nameTouched: true }
                                        : x
                                    )
                                  )
                                }
                                placeholder="Ej. INE Frente / Oficio autorización 2026"
                                className={cx(
                                  "mt-2",
                                  UI.input,
                                  !docNameOk && "border-rose-300"
                                )}
                                disabled={it.status === "uploading" || it.status === "done"}
                              />
                              {!docNameOk && (
                                <div className="mt-1 text-xs font-semibold text-rose-600">
                                  Obligatorio: mínimo 3 caracteres.
                                </div>
                              )}
                            </div>

                            {!!it.error && (
                              <div className="mt-2 text-xs font-semibold text-rose-700">
                                {it.error}
                              </div>
                            )}

                            <div className="mt-3">
                              <ProgressBar value={it.progress || 0} />
                              <div className="mt-1 text-right text-xs text-slate-500 tabular-nums">
                                {it.progress || 0}%
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col items-end gap-2">
                            {showUploadBtn && (
                              <Btn
                                onClick={() => uploadOne(it.id)}
                                disabled={!!contextError || totals.uploading > 0 || !docNameOk}
                                className="bg-slate-900 text-white hover:bg-black px-3 py-2 text-xs"
                              >
                                Subir
                              </Btn>
                            )}

                            {showCancelBtn && (
                              <Btn
                                onClick={() => cancelUpload(it.id)}
                                className={cx(UI.btnSecondary, "px-3 py-2 text-xs")}
                              >
                                Cancelar
                              </Btn>
                            )}

                            <Btn
                              onClick={() => removeItem(it.id)}
                              disabled={it.status === "uploading"}
                              className={cx(UI.btnSecondary, "px-3 py-2 text-xs")}
                            >
                              Quitar
                            </Btn>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              
            </div>
          </div>
        </div>
      </div>

      {/* ✅ Sticky Bottom Bar */}
      <div className="fixed inset-x-0 bottom-0 z-[60] border-t border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-slate-900">
                {totals.total} en cola
              </span>
              {totals.uploading > 0 && <Chip tone="info">Subiendo…</Chip>}
              {totals.error > 0 && <Chip tone="err">{totals.error} errores</Chip>}
              {totals.done > 0 && <Chip tone="ok">{totals.done} OK</Chip>}
            </div>
            <div className="text-xs text-slate-500 truncate">
              Proyecto: {expedienteId || "—"} · Tipo: {tipo || "—"} · Concurrencia:{" "}
              {concurrency}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Btn
              onClick={retryErrors}
              disabled={totals.uploading > 0 || (totals.error === 0 && totals.canceled === 0)}
              className={UI.btnSecondary}
            >
              Reintentar
            </Btn>

            <Btn
              onClick={clearAll}
              disabled={!hasFiles}
              className={UI.btnSecondary}
            >
              Limpiar
            </Btn>

            <Btn
              onClick={uploadAllParallel}
              disabled={!canUploadAll}
              className="bg-slate-900 text-white hover:bg-black shadow-sm"
              title={
                contextError ||
                (!canUploadAll && hasFiles
                  ? "Falta Nombre del documento en uno o más archivos."
                  : "")
              }
            >
              Subir todo
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}
